/**
 * Cleans topic names by removing common prefixes to make them more readable for students
 * @param topicName - The original topic name
 * @returns The cleaned topic name
 */
export function cleanTopicName(topicName: string): string {
  if (!topicName) return topicName

  let cleaned = topicName.trim()

  // Remove common prefixes (case insensitive)
  cleaned = cleaned.replace(/^(Quiz|Practice|Topic):\s*/i, '')

  // Remove number prefixes like "01 ", "02 ", etc. (two digits followed by space)
  cleaned = cleaned.replace(/^\d{2}\s+/, '')

  return cleaned.trim()
}

/**
 * Cleans conversation titles for sidebar display - removes number prefixes but keeps type prefixes
 * Handles edge cases where users might have manually edited the title
 * @param conversationTitle - The original conversation title
 * @returns The cleaned title for sidebar display
 */
export function cleanConversationTitle(conversationTitle: string): string {
  if (!conversationTitle) return conversationTitle

  let cleaned = conversationTitle.trim()

  // Handle more complex cases with multiple patterns:
  // "Practice: Quiz: 01 Topic" -> "Practice: Topic"
  // "Practice: Quiz: Topic" -> "Practice: Topic"
  // "Practice: 01 Topic" -> "Practice: Topic"

  // Pattern 1: "Practice: Quiz: 01 Topic" (with number)
  const duplicatePrefixWithNumberPattern =
    /^(Practice|Quiz|Topic):\s*(Practice|Quiz|Topic):\s*\d{2}\s+(.+)/i
  let match = cleaned.match(duplicatePrefixWithNumberPattern)

  if (match) {
    const firstPrefix = match[1]
    const topicName = match[3]
    return `${firstPrefix}: ${topicName}`
  }

  // Pattern 2: "Practice: Quiz: Topic" (without number)
  const duplicatePrefixPattern =
    /^(Practice|Quiz|Topic):\s*(Practice|Quiz|Topic):\s*(.+)/i
  match = cleaned.match(duplicatePrefixPattern)

  if (match) {
    const firstPrefix = match[1]
    const topicName = match[3]
    return `${firstPrefix}: ${topicName}`
  }

  // Pattern 3: "Practice: 01 Topic" (single prefix with number)
  const prefixWithNumberPattern = /^(Practice|Quiz|Topic):\s*\d{2}\s+(.+)/i
  match = cleaned.match(prefixWithNumberPattern)

  if (match) {
    const prefix = match[1]
    const topicName = match[2]
    return `${prefix}: ${topicName}`
  }

  // Pattern 4: Standalone number prefixes "01 Topic"
  cleaned = cleaned.replace(/^\d{2}\s+/, '')

  return cleaned.trim()
}
